import React from "react";
import { createRoot } from "react-dom/client";
import WidgetPopup from "./components/widget-popup";
import "./global.css";

interface ConceroWidgetConfig {
  agentId: number;
  containerId?: string;
  token: string;
}

const CUSTOM_ALPHABET =
  "QoA9k2D#Cm!r6ZtN@b4sTgJ1pWxfV7EhYi5u0U8M$LvnHP3qySRcKlOdFBGeXzw";

const ALPHABET_MAP: Record<string, number> = {};
for (let i = 0; i < CUSTOM_ALPHABET.length; i++) {
  ALPHABET_MAP[CUSTOM_ALPHABET[i]] = i;
}
const XOR_KEY = 73;

export function customDecode(encoded: string): string {
  const raw = atob(encoded);
  const bytes = new Uint8Array(raw.length);

  for (let i = 0; i < raw.length; i++) {
    const idx = ALPHABET_MAP[raw[i]];
    const x = idx % 256;
    bytes[i] = x ^ XOR_KEY; // reverse XOR
  }

  const decoder = new TextDecoder();
  return decoder.decode(bytes);
}

class ConceroWidget {
  private root: ReturnType<typeof createRoot> | null = null;
  private config: ConceroWidgetConfig;

  constructor(config: ConceroWidgetConfig) {
    this.config = config;
  }

  render(): void {
    const targetContainerId = this.config.containerId;
    const container = targetContainerId
      ? document.getElementById(targetContainerId)
      : document.body;

    if (!container) {
      console.error(
        `Concero Widget: Container with id "${targetContainerId}" not found`,
      );
      return;
    }

    // Clean up existing instance if any
    if (this.root) {
      this.destroy();
    }

    try {
      const config = JSON.parse(customDecode(this.config.token)) as {
        agentId: number;
        isLocalhost: "true" | "false";
        env: string;
        commit: string;
        token: string;
      };

      this.root = createRoot(container);
      this.root.render(
        <React.StrictMode>
          <WidgetPopup>
            <iframe
              className="w-full h-full"
              src={`https://widget${import.meta?.env?.VITE_API_PRODUCTION === "true" ? "" : "-dev"}.concero.ai/agent/${config.agentId}/?agentId=${config.agentId}&isLocalhost=${config.isLocalhost}&env=${config.env}&commit=${config.commit}&token=${config.token}`}
            ></iframe>
          </WidgetPopup>
        </React.StrictMode>,
      );
    } catch (error) {
      console.error("Concero Widget: Error decoding token", error);
    }
  }

  destroy(): void {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }

  updateConfig(newConfig: Partial<ConceroWidgetConfig>): void {
    this.config = { ...this.config, ...newConfig };
    // Re-render with new config
    this.render();
  }
}

// Auto-render function for simple usage
function renderConceroWidget(config: ConceroWidgetConfig): ConceroWidget {
  const widget = new ConceroWidget(config);
  widget.render();
  return widget;
}

// Global namespace
declare global {
  interface Window {
    ConceroWidget: typeof ConceroWidget;
    renderConceroWidget: typeof renderConceroWidget;
  }
}

// Export to global window object
if (typeof window !== "undefined") {
  window.ConceroWidget = ConceroWidget;
  window.renderConceroWidget = renderConceroWidget;
}

// ES module exports
export { ConceroWidget, renderConceroWidget };
export type { ConceroWidgetConfig };
